<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue';
import { VDataTable } from 'vuetify/components';
import { useDisplay } from 'vuetify';
import { CustomerMgmtTypes, useGetOrganizationUsers } from '@ilteducation/customer-mgmt';
import { getAllowedRoles } from '@ilteducation/customer-mgmt-permissions';

import { useLocale } from '../composables/useLocale';
import { useCurrentOrganization } from '../composables/useCurrentOrganization';
import { useGotoProfile } from '../composables/useGotoProfile';
import { useDefaultUserListRouteQuery } from '../composables/default';
import MainTable from '../el/MainTable.vue';
import GroupLabel from '../el/GroupLabel.vue';
import type { FetchItems } from '@ilteducation/components';
import { Prettify } from '../types';
import { Filters } from '../el/table/types';

/** The order the role filter has always offered, independent of what the registry allows. */
const ROLE_FILTER_ORDER = ['student', 'employee', 'admin', 'guardian'] as const;

const { t } = useLocale();
const { smAndDown } = useDisplay();
const { organization } = useCurrentOrganization();

const headers = computed((): VDataTable['$props']['headers'] => [
  // max width set to view width, percent dont work :(
  { title: t('lcp.name'), key: 'fullname', align: 'start', sortable: true, maxWidth: '20vw', nowrap: true },
  { title: t('lcp.username'), key: 'username', align: 'start', sortable: true, maxWidth: '20vw', nowrap: true },
  { title: t('lcp.role'), key: 'role', align: 'start', sortable: false, maxWidth: '10vw', nowrap: true },
  ...(organization.value.secureAuthEnabled
    ? [
        {
          title: t('lcp.secure_login'),
          key: 'hasMFAEnabled',
          align: 'start' as const,
          sortable: false,
          maxWidth: '10vw',
          nowrap: true,
        },
      ]
    : []),
  { title: t('lcp.groups'), key: 'groups', align: 'start', sortable: false, maxWidth: '10vw', nowrap: true },
]);

const mobileHeaders = computed((): VDataTable['$props']['headers'] => [
  { title: t('lcp.name'), key: 'fullname', align: 'start', sortable: true, maxWidth: '20vw', nowrap: true },
  { title: t('lcp.role'), key: 'role', align: 'start', sortable: false, maxWidth: '10vw', nowrap: true },
]);

const props = defineProps<{ organizationUid: string }>();
const { loadUsers } = useGetOrganizationUsers();

const { param: userListQuery, setValue: setUserListQuery } = useDefaultUserListRouteQuery();
const { gotoGroupProfile } = useGotoProfile();

watch(
  () => props.organizationUid,
  () => {
    setUserListQuery({
      ...userListQuery.value,
      page: 1,
      searchString: undefined,
      sortBy: undefined,
      sortOrder: undefined,
    });
  },
);

type UserListItem = CustomerMgmtTypes.GetOrganizationUsersResponse['data'][number];

const itemsPerPage = computed(() => {
  return userListQuery.value?.pageSize !== undefined && userListQuery.value?.pageSize !== null
    ? Number(userListQuery.value.pageSize)
    : undefined;
});

const fetchItems: FetchItems<typeof loadUsers> = async (args) => {
  if (!props.organizationUid) {
    return {
      data: [],
      meta: {
        totalItems: 0,
      },
    } satisfies CustomerMgmtTypes.GetOrganizationUsersResponse;
  }

  return loadUsers(props.organizationUid, {
    pageNumber: args.pageNumber,
    pageSize: args.pageSize,
    sortBy: args.sortBy as CustomerMgmtTypes.SearchUsersRequest['sortBy'],
    sortOrder: args.sortOrder,
    q: args.q,
    filters: args.filters,
  });
};

const theTable = ref();

const filters = computed(
  (): Prettify<Filters<keyof CustomerMgmtTypes.GetOrganizationUsersRequest['filters']>> => ({
    roles: {
      type: 'enum',
      labelTransKey: 'lcp.role',
      multiple: true,
      // Only the roles that can exist in this kind of organization are worth filtering by,
      // listed in the order the filter has always shown rather than the registry's.
      options: ROLE_FILTER_ORDER.filter((role) => getAllowedRoles(organization.value).includes(role)),
    },
    mfaEnabled: {
      type: 'boolean',
      labelTransKey: 'lcp.mfa_enabled',
      default: undefined,
      selectionTransKeys: {
        false: 'lcp.filter_only_users_without_mfa',
        true: 'lcp.filter_only_users_with_mfa',
        intermediate: 'lcp.filter_no_filter',
      },
    },
  }),
);

const model = defineModel<string[]>({ default: [] });

defineExpose({
  reloadUsers: async () => {
    await nextTick();
    await new Promise((resolve) => setTimeout(resolve, 5000));
    theTable?.value?.reloadItems();
  },
});
</script>
<template>
  <MainTable
    list="users"
    ref="theTable"
    v-model="model"
    :headers="headers"
    :mobile-headers="mobileHeaders"
    :itemsPerPage="itemsPerPage"
    :sort-order="userListQuery?.sortOrder"
    :sort-by="userListQuery?.sortBy"
    :search-string="userListQuery?.searchString"
    :fetchItems="fetchItems"
    :organizationUid="organizationUid"
    :filters="filters"
    show-select
    selectStrategy="page"
    :enable-footer-panel="smAndDown"
  >
    <template #[`item.role`]="{ item }">
      <span class="mr-2">{{ t(`lcp.role_${(item as UserListItem).association.role}`) }}</span>
    </template>

    <template #[`item.hasMFAEnabled`]="{ item }">
      <v-icon>{{ (item as UserListItem).hasMFAEnabled ? 'mdi-account-lock' : '' }}</v-icon>
    </template>

    <template #[`item.groups`]="{ item }">
      <div class="d-flex flex-nowrap ga-2">
        <v-chip
          v-for="membership in (item as UserListItem).association.organization.memberships.slice(0, 2)"
          :key="membership.groupUid"
          @click.stop="gotoGroupProfile(membership.groupUid)"
        >
          <GroupLabel :name="membership.group.name" :years-in-school="membership.group.yearsInSchool" size="small" />
        </v-chip>
        <v-chip v-if="(item as UserListItem).association.organization.memberships.length > 2">...</v-chip>
      </div>
    </template>
  </MainTable>
</template>
